import { useCallback } from 'react';
import { IconButton } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  FormProvider,
  Iconify,
  MenuPopover,
  RowActions as MenuRowActions,
  useMenuPopover,
} from '@app/components';
import { useDialog, useRequestState } from '@app/hooks';
import { Strings } from '@app/constants';
import { IIntern } from '../types';
import { selectPromoteAsEmployeeState, selectUpdateInternStatusState } from '../selectors';
import { updateInternStatus, promoteAsEmployee } from '../slice';
import { ConfirmationDialog, DeleteDialog } from '@app/components/dialog';
import { AlertDialog } from './alert-dialog';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { AddEmployeePage } from '@app/modules/employees';
import { updateCandidateStatusSchema } from '@app/modules/candidates/validators';

interface Props {
  row: IIntern;
}

const RowActions = ({ row }: Props) => {
  const menuPopover = useMenuPopover();
  const editDialog = useDialog();
  const activeInactiveDialog = useDialog();
  const promoteAsEmployeeDialog = useDialog();
  const alertDialog = useDialog();
  const newStatus =
    row.status === Strings.status.active ? Strings.status.inactive : Strings.status.active;
  const actions = [
    { label: Strings.rowActions.edit, callback: editDialog.show },
    {
      label: newStatus,
      callback: activeInactiveDialog.show,
    },
    {
      label: Strings.rowActions.promoteAsEmployee,
      callback: () => {
        if (row.status === Strings.status.inactive) {
          alertDialog.show();
        } else {
          promoteAsEmployeeDialog.show();
        }
      },
    },
  ];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const methods = useForm({
    resolver: yupResolver(updateCandidateStatusSchema) as any,
  });
  const { loading } = useRequestState({
    stateSelector: selectUpdateInternStatusState,
    successMessageShown: true,
    onSuccess: activeInactiveDialog.hide,
    errorShown: true,
  });

  const updateInternStatusHandle = (reason: string) => {
    dispatch(
      updateInternStatus({
        row,
        status: newStatus,
        reason,
      })
    );
  };
  const navigateToEmployeeList = useCallback(() => {
    promoteAsEmployeeDialog.hide();
    navigate(PATH_DASHBOARD.employeeList);
  }, []);
  const { loading: navigateEmployee } = useRequestState({
    stateSelector: selectPromoteAsEmployeeState,
    onSuccess: navigateToEmployeeList,
    successMessageShown: true,
    errorShown: true,
  });
  const updateAsEmployee = () => {
    dispatch(
      promoteAsEmployee({
        id: row?.id,
        row,
        roleId: 5,
      })
    );
  };

  return (
    <>
      <IconButton onClick={menuPopover.show}>
        <Iconify icon="eva:more-vertical-fill" />
      </IconButton>

      <MenuPopover
        open={menuPopover.rowMenuAnchorElement}
        arrow="right-top"
        sx={{ width: 180 }}
        onClose={menuPopover.hide}
      >
        <MenuRowActions actions={actions} onClickAction={menuPopover.hide} />
      </MenuPopover>
      <FormProvider methods={methods}>
        <ConfirmationDialog
          title={`${Strings.common.activeInactiveTitle(row.status === Strings.status.active)}  ${
            Strings.field.intern
          }`}
          confirmationButtonTitle={Strings.button.confirm}
          caption={Strings.common.activeInactiveCaption(
            row.status === Strings.status.active,
            Strings.field.intern
          )}
          loading={loading}
          visible={activeInactiveDialog.visible}
          onClickConfirm={updateInternStatusHandle}
          onClose={activeInactiveDialog.hide}
        />
      </FormProvider>
      {editDialog.visible && (
        <AddEmployeePage open onClose={editDialog.hide} row={row} title={'intern'} />
      )}
      <DeleteDialog
        visible={promoteAsEmployeeDialog.visible}
        title={Strings.rowActions.promoteAsEmployee}
        caption={Strings.common.promoteAsEmployeeCaption}
        deleteButtonTitle={Strings.button.confirm}
        onClickDelete={updateAsEmployee}
        onClose={promoteAsEmployeeDialog.hide}
        loading={navigateEmployee}
      />
      {alertDialog.visible && <AlertDialog open onClose={alertDialog.hide} />}
    </>
  );
};

export { RowActions };
